import { useId, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { Id } from "../../../api/types";
import { addByHandPath } from "../../../app/paths";
import { Breadcrumb, Button, Card, ProgressRing, StateBadge } from "../../../ui";
import { ancestorsOf, crumbsOf, levelWords, progressWords, type NavModel } from "../model/nav";

interface NodeDetailProps {
  model: NavModel;
  id: Id;
  expanded: ReadonlySet<Id>;
  onToggle(id: Id): void;
  /** The person is an engineer, so a session can be started from here. */
  canStartSession: boolean;
  /** A past moment is shown: the links lead to the present. */
  replaying: boolean;
}

/** What is known about the node that is chosen, and the ways to go on from it. */
export function NodeDetail({ model, id, expanded, onToggle, canStartSession, replaying }: NodeDetailProps) {
  const node = model.byId.get(id);
  const navigate = useNavigate();
  const goalId = useId();
  const [goal, setGoal] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const start = useApiCommand("start_session");

  if (!node) return null;
  const children = model.childrenOf.get(node.id) ?? [];
  const progress = model.progress.get(node.id) ?? null;
  const isItem = node.kind === "item";
  const open = expanded.has(node.id);

  // What an engineer can add right here: the place in the family tree is filled in on the form (UI-02, CP-05).
  const chain = [...ancestorsOf(model, node.id), node];
  const library = chain.find((entry) => entry.kind === "library")?.id ?? null;
  const family = chain.find((entry) => entry.kind === "family")?.id ?? null;
  const subFamily = chain.find((entry) => entry.kind === "sub_family")?.id ?? null;
  const addHere: Array<{ label: string; to: string }> = [];
  if (node.kind === "library") {
    addHere.push({ label: "Add a family here", to: addByHandPath({ kind: "family", library }) });
    addHere.push({ label: "Add an item here", to: addByHandPath({ kind: "item", library }) });
  } else if (node.kind === "family") {
    addHere.push({ label: "Add a sub-family here", to: addByHandPath({ kind: "sub_family", family }) });
    addHere.push({ label: "Add an item here", to: addByHandPath({ kind: "item", library, family }) });
  } else if (node.kind === "sub_family") {
    addHere.push({ label: "Add an item here", to: addByHandPath({ kind: "item", library, family, subFamily }) });
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = goal.trim();
    if (!text) {
      setProblem("Write what you want to do on this item first.");
      return;
    }
    setProblem(null);
    try {
      const reply = await start.mutateAsync({ item_id: node.id, goal: text });
      void navigate(`/sessions/${encodeURIComponent(reply.data.session.id)}`);
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Card className="nv-detail" aria-labelledby={`${goalId}-title`}>
      <p className="nv-detail__level muted">{levelWords(model, node)}</p>
      <h2 id={`${goalId}-title`}>{node.label}</h2>
      <div className="row">
        {node.state && <StateBadge state={node.state} />}
        {isItem && (node.abstract || !node.drawn) && <span className="muted">Not drawn: it has no symbol and no map of its own.</span>}
      </div>
      <Breadcrumb crumbs={crumbsOf(model, node.id)} linkFor={(crumb) => (crumb.element_id ? `?select=${encodeURIComponent(crumb.element_id)}` : null)} />
      <p className="nv-detail__progress">
        <ProgressRing validated={progress?.validated ?? 0} waiting={progress?.waiting ?? 0} total={progress?.total ?? 0} size={40} />{" "}
        <span>{progressWords(progress)}</span>
      </p>
      {children.length > 0 && (
        <p>
          <Button variant="secondary" aria-expanded={open} onClick={() => onToggle(node.id)}>
            {open ? "Fold what is below it" : `Open what is below it (${model.below.get(node.id) ?? children.length})`}
          </Button>
        </p>
      )}
      {replaying && <p className="muted">You are looking at the past. These links open the present state.</p>}
      <p className="row">
        {isItem ? (
          <>
            <Link className="button button--secondary" to={`/items/${encodeURIComponent(node.id)}/map`}>
              Open its live map
            </Link>
            <Link className="button button--secondary" to={`/items/${encodeURIComponent(node.id)}`}>
              Open its page
            </Link>
          </>
        ) : (
          <Link className="button button--secondary" to={`/elements/${encodeURIComponent(node.id)}`}>
            Open its page
          </Link>
        )}
      </p>
      {canStartSession && !replaying && addHere.length > 0 && (
        <div className="nv-detail__add" role="group" aria-label="Add something here">
          <p className="row">
            {addHere.map((entry) => (
              <Link key={entry.label} className="button button--secondary" to={entry.to}>
                {entry.label}
              </Link>
            ))}
          </p>
        </div>
      )}
      {isItem && canStartSession && !replaying && (
        <form onSubmit={(event) => void submit(event)} className="nv-detail__start" aria-label="Start a session on this item">
          <label htmlFor={goalId} className="field__label">
            What do you want to do on this item?
          </label>
          <input id={goalId} className="field__input" value={goal} onChange={(event) => setGoal(event.target.value)} autoComplete="off" />
          {problem && (
            <p role="alert" className="field__error">
              {problem}
            </p>
          )}
          <p>
            <Button type="submit" variant="primary" busy={start.isPending}>
              Start a session
            </Button>
          </p>
        </form>
      )}
    </Card>
  );
}
